import { useNavigate } from "react-router-dom";
import { useCallback, useEffect } from "react";
import { buildSDPSearchPayload } from "app/utils/smartDPUtils";
import { QUOTATION_CODE_STATUS, RESOLUTION, HOTEL_ONLY_PRODUCTS_FILTERS_KEY } from "app/constants";
import PropTypes from "prop-types";
import addHours from "date-fns/addHours";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { unsetSDPProductsFilter } from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import HotelOnlySearchForm from "app/pages/HotelOnly/HotelOnlySearchForm/HotelOnlySearchForm";
import {
	fetchHotelOnlySearchFormAvailability,
	resetHotelOnlyBookingFlowId,
} from "app/pages/HotelOnly/hotelOnlyActionCreator";

const HotelOnlySearchFormContainer = props => {
	const {
		fetchHotelOnlySearchFormAvailability = () => {},
		unsetSDPProductsFilter = () => {},
		resetHotelOnlyBookingFlowId = () => {},
		destinations = [],
	} = props;

	const navigate = useNavigate();

	useEffect(() => {
		if (!destinations || destinations?.length === 0) {
			fetchHotelOnlySearchFormAvailability();
		}
	}, [destinations]);

	const handleFormSubmission = useCallback(({ values, pathname = "" }) => {
		const queryParams = buildSDPSearchPayload(values);
		unsetSDPProductsFilter(HOTEL_ONLY_PRODUCTS_FILTERS_KEY.BUDGET);

		// reset bookingFlowId on each new search for hotelOnly
		resetHotelOnlyBookingFlowId();

		if (pathname.includes("/hotelonly/listing")) {
			navigate({ pathname: pathname, search: queryParams }, { replace: true });
		} else {
			navigate({ pathname: "/hotelonly/listing", search: queryParams });
		}
	}, []);

	return (
		<div className="sdp-search-form__container">
			<HotelOnlySearchForm {...props} onSuccess={handleFormSubmission} />
		</div>
	);
};

HotelOnlySearchFormContainer.propTypes = {
	fetchHotelOnlySearchFormAvailability: PropTypes.func,
	resetHotelOnlyBookingFlowId: PropTypes.func,
	unsetSDPProductsFilter: PropTypes.func,
	destinations: PropTypes.array,
	isMobile: PropTypes.bool,
	brand: PropTypes.string,
	disableFormPersistance: PropTypes.bool, // used to avoid fields flickering when coming from merch page with sdp form to home sdp
};

const mapStateToProps = (state, props) => {
	const booking = state.booking;

	const initialValues = {
		travelDates: {
			departureDate: "",
			endDate: "",
		},
	};

	if (
		!props.disableFormPersistance &&
		booking.destinationResort?.code &&
		state.smartDP.destinations
	) {
		const destination = state.smartDP.destinations.find(
			destination => destination.code.toString() === booking.destinationResort.code
		);
		if (destination) {
			initialValues.destinationResort = {
				...booking.destinationResort,
				departureDateStart: destination.departureDateStart,
				departureDateEnd: destination.departureDateEnd,
			};
		}
	}

	if (!props.disableFormPersistance && booking.departureDate) {
		// @see SmartDPSearch in handleFormSubmission
		// ex Paris : Fri Jun 11 2021 02:00:00 GMT+0200 (heure d’été d’Europe centrale)
		let departureDate = new Date(booking.departureDate);
		// => Fri Jun 11 2021 00:00:00 GMT+0200 (heure d’été d’Europe centrale)
		departureDate = addHours(departureDate, departureDate.getTimezoneOffset() / 60);
		// departureDate.geTime() gives 10/6/2021 à 22:00:00
		initialValues.travelDates.departureDate = departureDate;
	}

	if (!props.disableFormPersistance && booking.endDate) {
		// @see SmartDPSearch in handleFormSubmission
		// ex Paris : Fri Jun 11 2021 02:00:00 GMT+0200 (heure d’été d’Europe centrale)
		let endDate = new Date(booking.endDate);
		// => Fri Jun 11 2021 00:00:00 GMT+0200 (heure d’été d’Europe centrale)
		endDate = addHours(endDate, endDate.getTimezoneOffset() / 60);
		// endDate.geTime() gives 10/6/2021 à 22:00:00
		initialValues.travelDates.endDate = endDate;
	}

	if (booking.occupancies?.length > 0) {
		initialValues.occupancies = booking.occupancies;
	}

	if (!props.disableFormPersistance && booking.duration?.value) {
		initialValues.duration = {
			code: booking.duration.value,
		};
	}

	return {
		initialValues,
		destinations: state.smartDP.destinations,
		validateOnMount: state.smartDP.status === QUOTATION_CODE_STATUS.BAD_REQUEST,
		isMobile: [RESOLUTION.SMALL, RESOLUTION.MEDIUM].includes(state?.resolution),
		brand: state.brand.code,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchHotelOnlySearchFormAvailability,
			unsetSDPProductsFilter,
			resetHotelOnlyBookingFlowId,
		},
		dispatch
	);
};

export default injectIntl(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(HotelOnlySearchFormContainer)
);
